import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Button, Space, Modal, Form, Input, InputNumber, App, Switch, Typography, Select, Breadcrumb, Tooltip, theme, QRCode, Card, Row,Col } from "antd";
import { PlusOutlined, EditOutlined, DownloadOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { useAuth } from "../../context/AuthProvider";
import { getBackendUrl } from '../../utils/env';
import { QRCodeCanvas } from 'qrcode.react';
import { toCanvas } from "qrcode";

import axios from "axios";
import ForbiddenPage from "../Status/ForbiddenPage";
import dayjs from "dayjs";


const { Title, Text } = Typography;

export default function AdminQRDashboard() {
  const { message } = App.useApp();
  const { user } = useAuth();
  const { token } = theme.useToken();
  const [qrCodes, setQrCodes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingQR, setEditingQR] = useState(null);
  const [tempCode, setTempCode] = useState("");
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedActivities, setSelectedActivities] = useState([]);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewCode, setPreviewCode] = useState(null);

  const navigate = useNavigate();

  const cardStyle = {
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowTertiary,
    backgroundColor: token.colorBgContainer,
  };

  const filteredQRCodes = qrCodes
    .filter(qr => selectedActivities.length === 0 || selectedActivities.includes(qr.activity_id))
    .filter(qr => qr.code.toLowerCase().includes(searchTerm.toLowerCase()));


  const handlePreview = (code) => {
    setPreviewCode(code);
    setPreviewVisible(true);
  };


  useEffect(() => {
    if (user?.id === 1) {
      fetchQRCodes();
      fetchActivities();
    }
  }, [user]);

  const fetchActivities = () => {
    axios.get(`${getBackendUrl()}api/admin/activities.php`)
      .then(res => {
        if (res.data.success) {
          setActivities(res.data.activities);
        } else {
          message.error("No se pudieron cargar las actividades");
        }
      })
      .catch(() => message.error("Error al cargar las actividades"));
  };



  const fetchQRCodes = () => {
    setLoading(true);
    axios.get(`${getBackendUrl()}api/admin/admin_qr_manager.php`)
      .then(res => {
        if (res.data.success) {
          setQrCodes(res.data.qrcodes);
        } else {
          message.error("No se pudieron cargar los códigos QR");
        }
      })
      .catch(() => {
        message.error("Error al cargar los códigos QR");
      })
      .finally(() => setLoading(false));
  };

  const downloadQR = async (code) => {
    const qrSize = 512;
    const iconUrl = "/img/logo-negro.png";

    const canvas = document.createElement("canvas");
    canvas.width = qrSize;
    canvas.height = qrSize;
    const ctx = canvas.getContext("2d");

    try {
      await toCanvas(canvas, code, {
        width: qrSize,
        margin: 2,

      });

      const icon = new Image();
      icon.src = iconUrl;
      icon.onload = () => {
        const iconSize = qrSize * 0.2;
        const x = (qrSize - iconSize) / 2;
        const y = (qrSize - iconSize) / 2;
        ctx.drawImage(icon, x, y, iconSize, iconSize);

        const url = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.href = url;
        downloadLink.download = `qr_${code}_HD.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      };
    } catch (err) {
      console.error("Error al generar el QR:", err);
      message.error("Error al generar el QR");
    }
  };


  const generateCode = (activityId) => {
    const activity = activities.find(a => a.id === activityId);
    if (!activity) return;

    const today = dayjs().format("YYYY-MM-DD");
    const random = Math.floor(100000 + Math.random() * 900000);

    const code = `${activity.name}_${today}_${random}`
      .replace(/\s+/g, "_")
      .toUpperCase();

    form.setFieldsValue({ code });
    setTempCode(code);
  };


  const sendAction = (payload, successMsg) => {
    axios.post(`${getBackendUrl()}api/admin/admin_qr_manager.php`, {
      ...payload,
      action: payload.action || (editingQR ? "update" : "create"),
    })
      .then(res => {
        if (res.data.success) {
          message.success(successMsg);
          fetchQRCodes();
          setModalVisible(false);
          form.resetFields();
        } else {
          message.error(res.data.message || "Error en la operación");
        }
      })
      .catch(() => message.error("Error en la operación"));
  };

  const handleEdit = (record) => {
    setEditingQR(record);
    form.setFieldsValue(record);
    setTempCode(record.code);
    setModalVisible(true);
  };

  const handleDelete = (id) => {
    sendAction({ action: "delete", id }, "QR eliminado");
  };

  const handleToggleActive = (record) => {
    sendAction({ action: "toggle", id: record.id, active: record.is_active ? 0 : 1 }, "Estado actualizado");
  };

  const handleModalSubmit = () => {
    form.validateFields().then(values => {
      const payload = {
        ...values,
        action: editingQR ? "update" : "create",
      };
      if (editingQR) payload.id = editingQR.id;

      sendAction(payload, editingQR ? "QR actualizado" : "QR creado");
      setModalVisible(false);
      form.resetFields();
    });
  };

  if (user?.id !== 1) return <ForbiddenPage />;

  return (
    <div style={{ 
      padding: 'clamp(16px, 4vw, 32px)', 
      maxWidth: 1200, 
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: token.colorBgLayout 
    }}>
      
      {/* NAVEGACIÓN */}
      <Breadcrumb
        style={{ marginBottom: 24 }}
        items={[
          { title: <span onClick={() => navigate("/admin")} style={{ cursor: "pointer", color: token.colorPrimary }}>Panel de Administración</span> },
          { title: "Gestión de QR" },
        ]}
      />

      {/* CABECERA DINÁMICA */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>Gestión de Códigos QR</Title>
          <Text type="secondary">Crea y supervisa los puntos de acceso mediante códigos QR.</Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => {
              form.resetFields();
              setEditingQR(null);
              setTempCode("");
              setModalVisible(true);
            }}
            style={{ 
              borderRadius: token.borderRadius,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)' 
            }}
          >
            Crear nuevo QR
          </Button>
        </Col>
      </Row>

      {/* FILTROS Y TABLA */}
      <Card style={cardStyle} bodyStyle={{ padding: 24 }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={24} md={10}>
            <Input
              placeholder="Buscar por código..."
              prefix={<SearchOutlined style={{ color: token.colorTextDisabled }} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="large"
            />
          </Col>
          <Col xs={24} md={10}>
            <Select
              placeholder="Filtrar por actividad"
              mode="multiple"
              allowClear
              suffixIcon={<FilterOutlined />}
              style={{ width: '100%' }}
              size="large"
              value={selectedActivities}
              onChange={(value) => setSelectedActivities(value)}
            >
              {activities.map((act) => (
                <Select.Option key={act.id} value={act.id}>{act.name}</Select.Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Table
          dataSource={filteredQRCodes}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 8, showTotal: (total) => `Total: ${total} QRs` }}
          scroll={{ x: "max-content" }}
          size="middle"
        >
          <Table.Column 
            title="Código" 
            dataIndex="code" 
            render={(code) => <Text code>{code}</Text>} 
          />
          <Table.Column title="Actividad" dataIndex="activity_name" />
          <Table.Column 
            title="Bravos" 
            dataIndex="points" 
            render={(pts) => <Text strong style={{ color: token.colorSuccess }}>{pts} bravos</Text>}
          />
          <Table.Column
            title="Estado"
            render={(record) => (
              <Switch
                checked={!!record.is_active}
                onChange={() => handleToggleActive(record)}
                checkedChildren="Activo"
                unCheckedChildren="Inactivo"
              />
            )}
          />
          <Table.Column
            title="Vista Previa"
            align="center"
            render={(record) => (
              <div 
                onClick={() => handlePreview(record.code)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handlePreview(record.code);
                  }
                }}
                role="button"
                tabIndex={0}
                style={{ 
                  cursor: "pointer", 
                  display: "inline-block",
                  padding: 8,
               // El QR siempre requiere fondo blanco para lectura
                  
                }}
              >
                <QRCode
                  value={record.code}
                  size={48}
                  bordered={true}
                  errorLevel="H"
                />
              </div>
            )}
          />
          <Table.Column
            title="Acciones"
            render={(record) => (
              <Space>
                <Tooltip title="Editar">
                  <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                </Tooltip>
              </Space>
            )}
          />
        </Table>
      </Card>

      {/* MODAL DE PREVIEW MEJORADO */}
      <Modal
        open={previewVisible}
        footer={null}
        centered
        onCancel={() => setPreviewVisible(false)}
        width={400}
      >
        {previewCode && (
          <div style={{ textAlign: "center", padding: '20px 0' }}>
            <Title level={4} style={{ marginBottom: 20 }}>Código de Acceso</Title>
            <div style={{ 
              background: '#ffffff', // Fondo blanco para asegurar legibilidad
              padding: 24, 
              borderRadius: token.borderRadiusLG,
              display: 'inline-block',
              boxShadow: token.boxShadowSecondary
            }}>
              <QRCode
                 bgColor="#ffffff" // FORZAR FONDO BLANCO
                color="#000000"
                value={previewCode}
                size={240}
                icon="/img/logo-negro.png"
                iconSize={50}
                errorLevel="H"
                bordered={false}
              />
            </div>
            <div style={{ marginTop: 24 }}>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                size="large"
                onClick={() => downloadQR(previewCode)}
                block
                style={{ backgroundColor: token.colorWarning, borderColor: token.colorWarning, color: '#000' }}
              >
                Descargar Imagen
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title={editingQR ? "Editar QR" : "Crear QR"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleModalSubmit}
        okText={editingQR ? "Guardar cambios":"Crear nuevo"}
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical">



          {/* Actividad */}
          <Form.Item
            name="activity_id"
            label="Actividad"
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Selecciona una actividad"
              onChange={generateCode}
            >
              {activities.map(act => (
                <Select.Option key={act.id} value={act.id}>
                  {act.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Puntos */}
          <Form.Item
            name="points"
            label="Bravos"
            rules={[{ required: true, type: 'number' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>


          {/* Código generado automáticamente */}
          <Form.Item
            name="code"
            label="Código"
            rules={[{ required: true }]}
          >
            <Input disabled />
          </Form.Item>

          {/* Preview QR */}
          {tempCode && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
                padding: 12,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                background: "#fff",
              }}
            >
              <QRCode
                value={tempCode}
                size={128}
                icon="/img/logo-negro.png"
                color="#000000"        //  FORZAR COLOR
                bgColor="#ffffff"
                iconSize={48}
                errorLevel="H"
                bordered={false}
                style={{ marginBottom: 8 }}
              />
              <div style={{ fontSize: 12, color: "#000" }}>
                Previsualización del QR
              </div>
            </div>
          )}

        </Form>
      </Modal>

    </div>
  );
}
